import { useCallback } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useIsItemSelected, useIsItemSelectable } from '../../../../common/context/items';
import type { ItemId } from '../../../../common/types/item.tsx';

export const useItemSelect = (id: ItemId, isMultiSelectEnabled: boolean) => {
	const [selected = false, { toggleItem, activateItem }]: ReturnType<typeof useIsItemSelected> =
		useIsItemSelected(id);
	const [selectable = false] = useIsItemSelectable(id);

	const handleClick = useCallback(
		(
			{
				ctrlKey,
				metaKey,
				shiftKey,
				currentTarget,
			}: { ctrlKey?: boolean; metaKey?: boolean; shiftKey: boolean; currentTarget: EventTarget },
			analyticsEvent: UIAnalyticsEvent,
		) => {
			const isCheckbox =
				currentTarget instanceof HTMLInputElement && currentTarget.type === 'checkbox';

			// Only toggle the item when it's a checkbox or user using key modifiers
			const shouldToggleItem =
				isMultiSelectEnabled && (isCheckbox || ctrlKey || metaKey || shiftKey);

			if (!shouldToggleItem) {
				return activateItem(id);
			}

			if (!selectable) {
				return;
			}

			// Multiselect
			return toggleItem(id, {
				analyticsEvent,
				withRange: shiftKey,
			});
		},
		[isMultiSelectEnabled, activateItem, id, selectable, toggleItem],
	);

	return { selectable, selected, handleClick };
};
